import axios, { AxiosResponse, AxiosRequestConfig } from 'axios';
import config from './config';
import { getErrorMessage } from './errorHandling';

let headers = {};

axios.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response && error.response.status === 401) {
      window.parent.location.href = config.sessionExpirationRedirectUrl;
    }

    if (axios.isAxiosError(error) && error.response) {
      if (error.response.data) {
        throw new Error(getErrorMessage(error.response.data));
      }
      throw new Error(error.message);
    } else {
      throw new Error('An unexpected error occurred');
    }
  }
);

export async function get<T>(path: string, responseType?: string): Promise<T> {
  if (config.authToken) {
    headers = {
      'Content-Type': 'application/json',
      Authorization: `bearer ${config.authToken}`,
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
      Expires: '0'
    };
  } else {
    headers = {
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
      Expires: '0'
    };
  }

  const axiosConfig: AxiosRequestConfig = {
    headers: headers
  };

  if (responseType) {
    axiosConfig.responseType =
      responseType as AxiosRequestConfig['responseType'];
  }

  const fullUrl = `${config.proxyApiUrl}/api/${path}`;
  const response: AxiosResponse<T> = await axios.get<T>(fullUrl, axiosConfig);
  return response.data;
}

export async function post<T, T2>(path: string, data: T2): Promise<T> {
  let postUrl = '';

  if (config.authToken !== null && config.authToken !== undefined) {
    headers = {
      'Content-Type': 'application/json',
      Authorization: `bearer ${config.authToken}`
    };
  } else {
    headers = {
      'Content-Type': 'application/json'
    };
  }
  if (path.indexOf('http://') === 0 || path.indexOf('https://') === 0) {
    postUrl = path;
  } else {
    postUrl = `${config.proxyApiUrl}/api/${path}`;
  }
  const fullUrl = postUrl;
  const response: AxiosResponse<T> = await axios.post<T>(fullUrl, data, {
    headers: headers
  } as AxiosRequestConfig);
  return response.data;
}

export async function put<T, T2>(path: string, data: T2): Promise<T> {
  const fullUrl = `${config.proxyApiUrl}/api/${path}`;
  if (config.authToken !== null && config.authToken !== undefined) {
    headers = {
      'Content-Type': 'application/json',
      Authorization: `bearer ${config.authToken}`
    };
  } else {
    headers = {
      'Content-Type': 'application/json'
    };
  }
  const response: AxiosResponse<T> = await axios.put<T>(fullUrl, data, {
    headers: headers
  } as AxiosRequestConfig);
  return response.data;
}

export async function remove<T>(path: string): Promise<T> {
  const fullUrl = `${config.proxyApiUrl}/api/${path}`;
  if (config.authToken !== null && config.authToken !== undefined) {
    headers = {
      'Content-Type': 'application/json',
      Authorization: `bearer ${config.authToken}`
    };
  } else {
    headers = {
      'Content-Type': 'application/json'
    };
  }
  const response: AxiosResponse<T> = await axios.delete(fullUrl, {
    headers: headers
  } as AxiosRequestConfig);
  return response.data;
}

export const getFileUrl = async (
  path: string,
  data?: object
): Promise<string> => {
  const filePath = `${config.proxyApiUrl}/api/${path}`;

  if (!data) {
    return filePath;
  }
  if (config.authToken !== null && config.authToken !== undefined) {
    headers = {
      Authorization: `bearer ${config.authToken}`
    };
  }

  try {
    const response = await axios({
      url: `${config.proxyApiUrl}/api/${path}`,
      method: 'POST',
      responseType: 'blob',
      data: data,
      headers: headers
    });

    const blob = new Blob([response.data], { type: 'application/pdf' });
    const fileURL = URL.createObjectURL(blob);
    return fileURL;
  } catch (err) {
    if (axios.isAxiosError(err)) {
      throw new Error(err.message);
    } else {
      throw new Error('An unexpected error occurred');
    }
  }
};

export const getAttachmentUrl = (
  versionExternalId: string,
  attachmentId: string
) => {
  return `${config.proxyApiUrl}/api/DynamicWorkflowVersion/${versionExternalId}/Attachment/${attachmentId}`;
};

export const getFlag = async (flagName: string): Promise<boolean> => {
  try {
    const response = await get<boolean>(
      `DynamicWorkflowFlag?flagName=${flagName}`
    );
    return response;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(err);
    return false;
  }
};
